import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { jobsApi } from '../api/client'
import NewListForm from '../components/lists/NewListForm'
import ListsPanel from '../components/lists/ListsPanel'
import TcgHelpPanel from '../components/lists/TcgHelpPanel'
import CustomCardsPanel from '../components/lists/CustomCardsPanel'
import LayoutOptions from '../components/layout/LayoutOptions'
import PreviewModal from '../components/jobs/PreviewModal'
import { usePreview } from '../hooks/usePreview'
import { useLayoutStore } from '../stores/layoutStore'
import { getCustomBackFile } from '../stores/listsStore'
import type { DeckList } from '../stores/listsStore'
import type { Card, PreviewListInput, PreviewRequest, Tcg } from '../types'

/** Human-readable label per TCG (for mixed-order hints). */
const TCG_LABEL: Record<Tcg, string> = {
  mtg: 'Magic',
  yugioh: 'Yu-Gi-Oh!',
  onepiece: 'One Piece',
  lorcana: 'Lorcana',
  riftbound: 'Riftbound',
}

/**
 * Deck Builder — "Listas independientes" model.
 *
 * The user pastes decklists; each becomes an independent, named "Lista"
 * (a deck) persisted to localStorage. Lists are TEXT-ONLY (no card images
 * are ever loaded in the frontend — Cristian's decision). Each list can be
 * viewed, edited, and turned into a PDF, alone or combined with others.
 *
 * Lists carry their own TCG. When an order mixes games, the backend groups
 * the cards per game and returns a ZIP with one PDF per TCG.
 *
 * T-07: per-list print options — each list decides whether its sideboard is
 * included and whether the Riftbound rune section prints (``printRunes``,
 * sent per card as ``include_runes``), and can carry a custom back image
 * (uploaded through /jobs/mixed as a ``local:N`` back reference).
 */
export default function Builder() {
  const navigate = useNavigate()
  const layoutOptions = useLayoutStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  // Preview modal state ("resumen previo"): the pending generation context is
  // kept so "Confirmar y generar" can create the real job after the user
  // sees the plan.
  const { loading: previewLoading, error: previewError, preview, fetchPreview, resetPreview } = usePreview()
  const [previewOpen, setPreviewOpen] = useState(false)
  const [pendingLists, setPendingLists] = useState<DeckList[]>([])

  /**
   * Build the backend JobOptions. `tcg` is only sent for single-game orders;
   * for mixed orders the backend routes by each card's `game` field.
   * 
   * NOTE: We intentionally DON'T send block_size - let the backend calculate
   * the optimal size automatically for maximum efficiency.
   */
  const buildOptions = (tcg?: Tcg) => ({
    page_size: layoutOptions.pageSize,
    cards_per_page: layoutOptions.cardsPerPage,
    bleed_mm: layoutOptions.includeBleed ? 3.0 : 0,
    fronts_only: layoutOptions.frontsOnly,
    include_cut_lines: layoutOptions.includeCutLines,
    cut_line_color: layoutOptions.cutLineColor,
    cut_line_style: layoutOptions.cutLineStyle,
    cut_line_width: layoutOptions.cutLineWidth,
    cut_line_over_cards: layoutOptions.cutLineOverCards,
    ...(layoutOptions.maxPdfMb != null ? { max_pdf_mb: layoutOptions.maxPdfMb } : {}),
    ...(tcg ? { tcg } : {}),
    // DO NOT send block_size - let backend auto-calculate for maximum efficiency
  })

  /**
   * Flatten lists into a card array (T-07). Each card is tagged with its
   * list's game and carries its list's ``printRunes`` choice as
   * ``include_runes`` (so combined multi-list jobs keep each list's
   * choice), and the sideboard is included only when the list's own
   * ``includeSideboard`` flag says so.
   *
   * Returns the cards plus a parallel array of list ids (used to attach
   * per-list custom backs to the right cards).
   */
  const flattenWithMeta = (lists: DeckList[]): { cards: Card[]; listIds: string[] } => {
    const cards: Card[] = []
    const listIds: string[] = []
    for (const list of lists) {
      const includeSide = list.includeSideboard ?? true
      const printRunes = list.printRunes ?? true
      const push = (c: Card) => {
        cards.push({
          ...c,
          game: c.game ?? list.tcg,
          include_runes: printRunes,
        })
        listIds.push(list.id)
      }
      list.mainboard.forEach(push)
      if (includeSide) list.sideboard.forEach(push)
    }
    return { cards, listIds }
  }

  /**
   * Build the preview request for a set of lists (the same shape ``POST
   * /jobs/preview`` expects). Cards with a local image are NOT sent as list
   * cards: the backend counts them as ``extra_locals`` (they fill gaps in
   * the LAST job), so we only count them here. T-07: sideboards follow each
   * list's own ``includeSideboard`` flag.
   */
  const buildPreviewRequest = (lists: DeckList[]): PreviewRequest => {
    const previewLists: PreviewListInput[] = lists.map((list) => ({
      name: list.name,
      tcg: list.tcg,
      cards: [...list.mainboard, ...(list.includeSideboard ?? true ? list.sideboard : [])]
        .filter((c) => !c._localFile)
        .map((c) => ({ name: c.name, quantity: c.quantity })),
    }))
    const extraLocals = lists.reduce(
      (acc, list) =>
        acc +
        [...list.mainboard, ...(list.includeSideboard ?? true ? list.sideboard : [])]
          .filter((c) => c._localFile)
          .reduce((sum, c) => sum + c.quantity, 0),
      0
    )
    return {
      lists: previewLists,
      options: {
        cards_per_page: layoutOptions.cardsPerPage,
        fronts_only: layoutOptions.frontsOnly,
        extra_locals: extraLocals,
      },
    }
  }

  /**
   * "Generar PDF" entry point: ask the backend for the PLAN first and open
   * the summary modal. No job is created until the user confirms there.
   */
  const openPreview = async (lists: DeckList[]) => {
    const { cards } = flattenWithMeta(lists)
    if (cards.length === 0) {
      setError('No hay cartas para generar el PDF')
      return
    }
    setPendingLists(lists)
    setPreviewOpen(true)
    setError(null)
    await fetchPreview(buildPreviewRequest(lists))
  }

  /** Create a job from a set of lists and navigate to its progress page. */
  const createJob = async (lists: DeckList[]) => {
    const { cards, listIds } = flattenWithMeta(lists)
    if (cards.length === 0) {
      setError('No hay cartas para generar el PDF')
      return
    }

    // Separate cards with local images from those to resolve.
    const localCards = cards.filter((c) => c._localFile)

    // T-07: per-list custom backs (uploaded this session via the runtime map).
    const backEntries = lists
      .map((l) => ({ id: l.id, file: getCustomBackFile(l.id) }))
      .filter((e): e is { id: string; file: File } => Boolean(e.file))

    const games = new Set(cards.map((c) => c.game ?? 'mtg'))
    const tcg = games.size === 1 ? ([...games][0] as Tcg) : undefined
    setLoading(true)
    setError(null)
    try {
      let job
      if (localCards.length > 0 || backEntries.length > 0) {
        // Mixed job: upload local images (+ custom backs) and resolve the rest.
        const images: File[] = []
        // Rewrite local fronts to "local:N" references for the backend.
        const cardsWithRefs: Card[] = cards.map((c) => {
          if (c._localFile) {
            const idx = images.length
            images.push(c._localFile)
            return { ...c, image_url: `local:${idx}` }
          }
          return c
        })
        // T-07: attach each list's custom back to ALL of that list's cards
        // as a "local:N" back reference (the backend rewrites it to the
        // saved file path; the engine prefers it over the default back).
        for (const entry of backEntries) {
          const idx = images.length
          images.push(entry.file)
          for (let i = 0; i < cardsWithRefs.length; i += 1) {
            if (listIds[i] === entry.id) {
              cardsWithRefs[i] = { ...cardsWithRefs[i], back_image_path: `local:${idx}` }
            }
          }
        }
        job = await jobsApi.createMixed(cardsWithRefs, images, buildOptions(tcg))
      } else {
        job = await jobsApi.create(cards, buildOptions(tcg))
      }
      navigate(`/job/${job.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el job')
      setLoading(false)
    }
  }

  /** "Confirmar y generar" — the user accepted the plan; create the real job. */
  const handleConfirmGenerate = () => {
    setPreviewOpen(false)
    resetPreview()
    void createJob(pendingLists)
  }

  /** "Volver" — close the modal and let the user edit. */
  const handlePreviewBack = () => {
    setPreviewOpen(false)
    resetPreview()
  }

  /** "Generar sin resumen" — generate despite the preview failure (explicit). */
  const handleGenerateAnyway = () => {
    setPreviewOpen(false)
    resetPreview()
    void createJob(pendingLists)
  }

  /** PDF for a single list (per-list sideboard/runes/back options apply). */
  const handleGeneratePdf = (list: DeckList) => {
    void openPreview([list])
  }

  /** Combined PDF from several lists (one PDF per TCG if they differ). */
  const handleGenerateCombined = (lists: DeckList[]) => {
    void openPreview(lists)
  }

  return (
    <>
      {/* Help toggle button — small floating button, always visible */}
      <button
        type="button"
        onClick={() => setHelpOpen(!helpOpen)}
        className="fixed left-4 top-24 z-20 w-10 h-10 rounded-full bg-white border border-gray-300 shadow-md flex items-center justify-center text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
        title={helpOpen ? 'Ocultar ayuda' : 'Mostrar ayuda'}
        aria-expanded={helpOpen}
      >
        {helpOpen ? '✕' : '?'}
      </button>

      {/* Collapsible help panel — overlays content when open */}
      {helpOpen && (
        <aside className="fixed left-4 top-36 w-80 z-10 max-h-[calc(100vh-10rem)] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          <TcgHelpPanel />
        </aside>
      )}

      <div className="max-w-6xl mx-auto space-y-8 px-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Deck Builder</h1>
          <p className="text-gray-600 mt-2">
            Pega una decklist para crear una lista. Gestiona tus listas y genera PDFs.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Nueva lista</h2>
              <NewListForm />
            </div>

            <CustomCardsPanel />

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <ListsPanel
                onGeneratePdf={handleGeneratePdf}
                onGenerateCombined={handleGenerateCombined}
              />
            </div>
          </div>

          <div className="space-y-6">
            <LayoutOptions />

            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-lg">Estado</h3>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                  {error}
                </div>
              )}
              {loading && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
                  Creando job…
                </div>
              )}
              <p className="text-sm text-gray-500">
                Selecciona una lista (o varias) y pulsa «Generar PDF». El PDF se genera en el
                servidor a partir de los nombres de las cartas.
              </p>
              <p className="text-sm text-gray-500">
                Cada lista guarda su juego ({Object.values(TCG_LABEL).join(', ')}). Si combinas
                listas de juegos distintos, el servidor genera un PDF por juego y los entrega en
                un único ZIP. Cada lista decide sus sideboards, runas (Riftbound) y reverso.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-generation summary ("resumen previo") — shown before creating the job */}
      <PreviewModal
        isOpen={previewOpen}
        loading={previewLoading}
        error={previewError}
        preview={preview}
        onConfirm={handleConfirmGenerate}
        onBack={handlePreviewBack}
        onGenerateAnyway={handleGenerateAnyway}
        creating={loading}
      />
    </>
  )
}
